import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Save, FileText, AlertTriangle, Info, CheckCircle,
  Download, Power, PowerOff, ExternalLink
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VeriFactuSettings() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const shopId = user?.shop_id;

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [form, setForm] = useState({
    nif: '', razon_social: '', direccion_fiscal: '',
    ciudad: '', codigo_postal: '', invoice_series_prefix: 'A',
    print_qr: true, print_legend: true,
  });

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchSettings = useCallback(async () => {
    if (!shopId) return;
    try {
      const res = await fetch(`${API}/api/verifactu/settings/${shopId}`, { headers });
      const data = await res.json();
      setSettings(data);
      setForm({
        nif: data.nif || '',
        razon_social: data.razon_social || '',
        direccion_fiscal: data.direccion_fiscal || '',
        ciudad: data.ciudad || '',
        codigo_postal: data.codigo_postal || '',
        invoice_series_prefix: data.invoice_series_prefix || 'A',
        print_qr: data.print_qr !== false,
        print_legend: data.print_legend !== false,
      });
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [shopId, token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/verifactu/settings/${shopId}`, {
        method: 'PUT', headers, body: JSON.stringify(form)
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      const data = await res.json();
      setSettings(data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEnable = async () => {
    if (!acceptTerms) { toast.error('Please accept the authorization terms'); return; }
    if (!form.nif || !form.razon_social) { toast.error('NIF and Razon Social are required'); return; }
    setEnabling(true);
    try {
      // Save first
      await fetch(`${API}/api/verifactu/settings/${shopId}`, {
        method: 'PUT', headers, body: JSON.stringify(form)
      });
      const res = await fetch(`${API}/api/verifactu/settings/${shopId}/enable`, {
        method: 'POST', headers, body: JSON.stringify({ accept_terms: true })
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success('VeriFactu enabled!');
      fetchSettings();
    } catch (e) {
      toast.error(e.message || 'Failed to enable');
    } finally {
      setEnabling(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable VeriFactu? New invoices will not be generated.')) return;
    try {
      const res = await fetch(`${API}/api/verifactu/settings/${shopId}/disable`, {
        method: 'POST', headers
      });
      if (!res.ok) throw new Error('Failed to disable');
      toast.success('VeriFactu disabled');
      fetchSettings();
      setAcceptTerms(false);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDownloadAuth = async () => {
    try {
      const res = await fetch(`${API}/api/verifactu/settings/${shopId}/authorization/download`, { headers });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `autorizacion_verifactu_${form.nif}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleToggle = async (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    try {
      await fetch(`${API}/api/verifactu/settings/${shopId}`, {
        method: 'PUT', headers, body: JSON.stringify({ [field]: value })
      });
    } catch (e) { /* silent */ }
  };

  const isEnabled = settings?.enabled;
  const COLLABORATOR = 'VendFactura S.L.';

  if (loading) return (
    <ShopLayout>
      <div className="flex items-center justify-center h-64" data-testid="verifactu-settings-loading">
        <div className="w-8 h-8 border-4 border-[#1e3a5f]/20 border-t-[#1e3a5f] rounded-full animate-spin" />
      </div>
    </ShopLayout>
  );

  return (
    <ShopLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="verifactu-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#f97316]" />
              <h1 className="text-2xl font-bold text-[#1e3a5f]" data-testid="verifactu-settings-title">VeriFactu Settings</h1>
            </div>
            <p className="text-sm text-slate-500 mt-1">Spain Anti-Fraud Invoicing System Configuration</p>
          </div>
          <button
            onClick={() => navigate('/shop/verifactu')}
            className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm font-medium"
            data-testid="view-invoices-btn"
          >
            <FileText className="w-4 h-4" /> View Invoices
          </button>
        </div>

        {/* Status Banner */}
        {isEnabled ? (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
            data-testid="verifactu-active-banner"
          >
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-800">VeriFactu Active</p>
              <p className="text-sm text-green-700">Fiscal invoices are being generated automatically for all completed orders.</p>
            </div>
          </motion.div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="verifactu-inactive-banner">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">VeriFactu Not Active</p>
              <p className="text-sm text-amber-700">Configure your fiscal data below and enable VeriFactu to comply with Spain's anti-fraud regulations.</p>
            </div>
          </div>
        )}

        {/* Fiscal Data Card */}
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="fiscal-data-card">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-5 h-5 text-[#1e3a5f]" />
            <h2 className="text-lg font-bold text-[#1e3a5f]">Fiscal Data (Datos Fiscales)</h2>
          </div>
          <p className="text-sm text-slate-500 mb-5">Enter your Spanish tax information exactly as registered with AEAT</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">NIF (Tax ID) *</label>
              <input
                value={form.nif} onChange={e => setForm(f => ({ ...f, nif: e.target.value.toUpperCase() }))}
                disabled={isEnabled} placeholder="B12345678"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#f97316]/40"
                data-testid="nif-input"
              />
              <p className="text-xs text-slate-400 mt-1">Spanish tax identification number</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Invoice Series</label>
              <input
                value={form.invoice_series_prefix}
                onChange={e => setForm(f => ({ ...f, invoice_series_prefix: e.target.value.toUpperCase().slice(0, 2) }))}
                disabled={isEnabled} placeholder="A" maxLength={2}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#f97316]/40"
                data-testid="series-input"
              />
              <p className="text-xs text-slate-400 mt-1">Prefix for invoice numbers (e.g., A-00001)</p>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-semibold text-slate-700 mb-1">Razon Social (Legal Name) *</label>
            <input
              value={form.razon_social} onChange={e => setForm(f => ({ ...f, razon_social: e.target.value }))}
              disabled={isEnabled} placeholder="Mi Restaurante S.L."
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#f97316]/40"
              data-testid="razon-social-input"
            />
            <p className="text-xs text-slate-400 mt-1">Legal business name as registered</p>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-semibold text-slate-700 mb-1">Fiscal Address (Direccion Fiscal) *</label>
            <input
              value={form.direccion_fiscal} onChange={e => setForm(f => ({ ...f, direccion_fiscal: e.target.value }))}
              disabled={isEnabled} placeholder="Calle Principal 123"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#f97316]/40"
              data-testid="direccion-input"
            />
            <p className="text-xs text-slate-400 mt-1">Street address of registered business</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Postal Code (Codigo Postal) *</label>
              <input
                value={form.codigo_postal} onChange={e => setForm(f => ({ ...f, codigo_postal: e.target.value }))}
                disabled={isEnabled} placeholder="28001"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#f97316]/40"
                data-testid="postal-code-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">City (Ciudad) *</label>
              <input
                value={form.ciudad} onChange={e => setForm(f => ({ ...f, ciudad: e.target.value }))}
                disabled={isEnabled} placeholder="Madrid"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#f97316]/40"
                data-testid="ciudad-input"
              />
            </div>
          </div>

          {!isEnabled && (
            <button onClick={handleSave} disabled={saving}
              className="mt-5 flex items-center gap-2 px-5 py-2.5 bg-[#f97316] hover:bg-[#ea580c] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="save-settings-btn"
            >
              <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Settings'}
            </button>
          )}
        </div>

        {/* Receipt Options */}
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="receipt-options-card">
          <h2 className="text-lg font-bold text-[#1e3a5f] mb-1">Receipt Options</h2>
          <p className="text-sm text-slate-500 mb-5">Configure how VeriFactu data appears on receipts</p>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm text-slate-700">Print QR Code</p>
                <p className="text-xs text-slate-500">Show AEAT verification QR on receipts</p>
              </div>
              <Switch
                checked={form.print_qr}
                onCheckedChange={v => handleToggle('print_qr', v)}
                data-testid="print-qr-toggle"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm text-slate-700">Print VeriFactu Legend</p>
                <p className="text-xs text-slate-500">Show "VERI*FACTU - Factura verificable" text</p>
              </div>
              <Switch
                checked={form.print_legend}
                onCheckedChange={v => handleToggle('print_legend', v)}
                data-testid="print-legend-toggle"
              />
            </div>
          </div>
        </div>

        {/* Enable / Disable VeriFactu */}
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="enable-verifactu-card">
          {!isEnabled ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-5 h-5 text-[#f97316]" />
                <h2 className="text-lg font-bold text-[#1e3a5f]">Enable VeriFactu</h2>
              </div>
              <p className="text-sm text-slate-500 mb-4">Authorize {COLLABORATOR} to send fiscal invoices to AEAT on your behalf.</p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-800">
                  By enabling VeriFactu, you authorize {COLLABORATOR} as your fiscal collaborator
                  (colaborador social Tipo 017) to submit invoice records to the Spanish Tax Agency (AEAT)
                  on your behalf, in accordance with Royal Decree 1007/2023.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                <h3 className="font-bold text-sm text-slate-800 mb-2">AUTORIZACION / AUTHORIZATION</h3>
                <p className="text-sm text-slate-700 mb-2">
                  Yo, en calidad de representante legal de <strong>{form.razon_social || '[Su Empresa]'}</strong>
                  (NIF: <strong>{form.nif || '[NIF]'}</strong>), autorizo a {COLLABORATOR} a remitir los registros
                  de facturacion generados por mi sistema de facturacion a la Agencia Estatal de Administracion
                  Tributaria mediante el sistema VERI*FACTU.
                </p>
                <p className="text-sm text-slate-500 italic">
                  I, as the legal representative of the above entity, authorize {COLLABORATOR} to submit
                  invoice records generated by my invoicing system to the Spanish Tax Agency through the
                  VERI*FACTU system.
                </p>
              </div>

              <label className="flex items-start gap-3 mb-4 cursor-pointer" data-testid="accept-terms-checkbox">
                <input
                  type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-slate-300 text-[#f97316] focus:ring-[#f97316]"
                />
                <div>
                  <p className="font-semibold text-sm text-slate-700">I confirm and authorize (Confirmo y autorizo)</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    I confirm that the fiscal data provided is correct and complete. I authorize {COLLABORATOR} to
                    act as my fiscal collaborator for VERI*FACTU submissions to AEAT. A legally binding authorization
                    document will be generated and stored.
                  </p>
                </div>
              </label>

              <button
                onClick={handleEnable} disabled={enabling || !acceptTerms || !form.nif || !form.razon_social}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#f97316] hover:bg-[#ea580c] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="enable-verifactu-btn"
              >
                <Power className="w-4 h-4" /> {enabling ? 'Enabling...' : 'Enable VeriFactu'}
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-bold text-[#1e3a5f]">VeriFactu Enabled</h2>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Status:</span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">Active</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Next Invoice:</span>
                  <span className="font-mono text-sm text-slate-700">{settings?.invoice_series_prefix}-{String(settings?.next_invoice_number || 1).padStart(5, '0')}</span>
                </div>
                {settings?.authorization_reference && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Authorization:</span>
                    <span className="font-mono text-xs text-slate-600">{settings.authorization_reference}</span>
                  </div>
                )}
                {settings?.authorization_date && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Date:</span>
                    <span className="text-sm text-slate-600">{new Date(settings.authorization_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-green-700" />
                  <span className="text-sm font-medium text-green-800">Authorization Document</span>
                </div>
                <button onClick={handleDownloadAuth}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors"
                  data-testid="download-auth-btn"
                >
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </button>
              </div>

              <button onClick={handleDisable}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 hover:bg-red-100 text-red-700 rounded-lg text-sm font-medium transition-colors"
                data-testid="disable-verifactu-btn"
              >
                <PowerOff className="w-4 h-4" /> Disable VeriFactu
              </button>
            </>
          )}
        </div>

        {/* Footer Links */}
        <div className="flex items-center gap-6 text-sm">
          <a href="https://sede.agenciatributaria.gob.es" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-[#f97316] hover:underline"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Learn more about VeriFactu
          </a>
          <a href="https://www.agenciatributaria.es" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-slate-500 hover:text-slate-700 hover:underline"
          >
            <ExternalLink className="w-3.5 h-3.5" /> AEAT Official Website
          </a>
        </div>
      </div>
    </ShopLayout>
  );
}
